import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Badge,
  Spinner,
  Alert,
  Form,
  InputGroup,
  Row,
  Col,
  Modal,
  Pagination
} from 'react-bootstrap';
import { useNotifications } from '../contexts/NotificationContext';
import { usersAPI } from '../services/api.js';

const NotificationsTab = () => {
  const {
    notifications,
    unreadCount,
    loading,
    error,
    loadNotifications,
    markAsRead,
    markAllAsRead,
    isWsConnected,
    wsState,
    sendTestNotification
  } = useNotifications();

  const [filter, setFilter] = useState('all');
  const [selectedNotifications, setSelectedNotifications] = useState(new Set());
  const [showSendModal, setShowSendModal] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  // Данные для отправки уведомления
  const [notificationData, setNotificationData] = useState({
    user_id: '',
    title: '',
    message: '',
    notification_type: 'info',
    channels: ['websocket', 'telegram'],
    priority: 'normal',
    data: {},
    expire_at: null
  });

  useEffect(() => {
    loadNotifications();
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await usersAPI.getUsers({ limit: 100 });
      const usersList = response.users || response.data || response;
      setUsers(Array.isArray(usersList) ? usersList : []);
    } catch (err) {
      console.error('Error loading users:', err);
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const filteredNotifications = notifications.filter(notification => {
    if (filter === 'unread') return notification.status === 'unread';
    if (filter === 'read') return notification.status === 'read';
    return true;
  });

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedNotifications(new Set(filteredNotifications.map(n => n.id)));
    } else {
      setSelectedNotifications(new Set());
    }
  };

  const handleSelectNotification = (notificationId, isSelected) => {
    const newSelected = new Set(selectedNotifications);
    if (isSelected) {
      newSelected.add(notificationId);
    } else {
      newSelected.delete(notificationId);
    }
    setSelectedNotifications(newSelected);
  };

  const handleMarkSelectedAsRead = async () => {
    for (const notificationId of selectedNotifications) {
      await markAsRead(notificationId);
    }
    setSelectedNotifications(new Set());
  };

  // Функция для отправки уведомления
  const handleSendNotification = async () => {
    setSending(true);
    setSendResult(null);
    
    try {
      // Подготавливаем данные для отправки
      const dataToSend = {
        ...notificationData,
        user_id: parseInt(notificationData.user_id),
        // Если expire_at не указан, не включаем его
        ...(notificationData.expire_at && { expire_at: notificationData.expire_at })
      };

      const response = await fetch('https://taskit.hsbk.ru/api/v1/notifications/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'X-API-Key': 'web_site'
        },
        body: JSON.stringify(dataToSend)
      });

      const result = await response.json();
      
      if (response.ok) {
        setSendResult({ 
          success: true, 
          message: 'Уведомление успешно отправлено!',
          details: result
        });
        
        // Очищаем форму после успешной отправки
        setNotificationData({
          user_id: '',
          title: '',
          message: '',
          notification_type: 'info',
          channels: ['websocket', 'telegram'],
          priority: 'normal',
          data: {},
          expire_at: null
        });
        
        // Обновляем список уведомлений
        setTimeout(() => {
          loadNotifications();
        }, 1000);
      } else {
        setSendResult({ 
          success: false, 
          message: result.detail || 'Ошибка при отправке уведомления',
          details: result
        });
      }
    } catch (err) {
      console.error('Error sending notification:', err);
      setSendResult({ 
        success: false, 
        message: 'Ошибка сети при отправке уведомления',
        details: err.message
      });
    } finally {
      setSending(false);
    }
  };

  const handleInputChange = (field, value) => {
    setNotificationData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleChannelToggle = (channel) => {
    setNotificationData(prev => {
      const channels = prev.channels.includes(channel)
        ? prev.channels.filter(c => c !== channel)
        : [...prev.channels, channel];
      
      return { ...prev, channels };
    });
  };

  const getNotificationTypeVariant = (type) => {
    switch (type) {
      case 'success': return 'success';
      case 'warning': return 'warning';
      case 'error': return 'danger';
      case 'system': return 'primary';
      default: return 'info';
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'success': return 'bi-check-circle';
      case 'warning': return 'bi-exclamation-triangle';
      case 'error': return 'bi-x-circle';
      case 'system': return 'bi-gear';
      default: return 'bi-info-circle';
    }
  };

  const getStatusVariant = (status) => {
    return status === 'unread' ? 'primary' : 'secondary';
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Не указано';
    return new Date(dateString).toLocaleString('ru-RU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'Недавно';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'только что';
    if (diffMins < 60) return `${diffMins} мин назад`;
    if (diffHours < 24) return `${diffHours} ч назад`;
    if (diffDays < 7) return `${diffDays} дн назад`;
    
    return date.toLocaleDateString('ru-RU');
  };

  const getWsStateBadge = () => {
    switch (wsState) {
      case 'connected': return { variant: 'success', text: 'Подключен' };
      case 'connecting': return { variant: 'warning', text: 'Подключение...' };
      case 'disconnected': return { variant: 'danger', text: 'Отключен' };
      default: return { variant: 'secondary', text: 'Неизвестно' };
    }
  };

  const getFullName = (user) => {
    if (!user) return 'Не назначен';
    
    // Если пользователь имеет ошибку (заглушка)
    if (user.error) {
      return user.name || `Пользователь #${user.id}`;
    }
    
    const firstName = user.telegram_first_name || '';
    const lastName = user.telegram_last_name || '';
    
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else if (lastName) {
      return lastName;
    }
    
    return user.name || 'Пользователь';
  };

  const wsStateInfo = getWsStateBadge();

  if (loading && notifications.length === 0) {
    return (
      <Card>
        <Card.Body className="text-center py-5">
          <Spinner animation="border" variant="primary" className="mb-3" />
          <p className="text-muted">Загрузка уведомлений...</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <div className="notifications-tab">
      {/* Статистика и управление */}
      <Row className="g-3 mb-4">
        <Col md={3}>
          <Card className="border-0 bg-secondary text-white">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h4 className="mb-0">{notifications.length}</h4>
                  <small>Всего уведомлений</small>
                </div>
                <i className="bi bi-bell fs-2 opacity-50"></i>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 bg-warning text-dark">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h4 className="mb-0">{unreadCount}</h4>
                  <small>Непрочитанных</small>
                </div>
                <i className="bi bi-bell-fill fs-2 opacity-50"></i>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="border-0 bg-light">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="mb-1">WebSocket статус</h6>
                  <Badge bg={wsStateInfo.variant}>
                    {wsStateInfo.text}
                  </Badge>
                  {isWsConnected && (
                    <small className="text-muted ms-2">
                      {notifications.length > 0 ? `${notifications.length} уведомлений загружено` : 'Уведомлений нет'}
                    </small>
                  )}
                </div>
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => loadNotifications()}
                    title="Обновить уведомления"
                  >
                    <i className="bi bi-arrow-clockwise"></i>
                  </Button>
                  <Button
                    variant="outline-success"
                    size="sm"
                    onClick={markAllAsRead}
                    disabled={unreadCount === 0}
                    title="Отметить все как прочитанные"
                  >
                    <i className="bi bi-check-all me-1"></i>
                    Прочитать все
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setShowSendModal(true)}
                    title="Отправить уведомление"
                  >
                    <i className="bi bi-send me-1"></i>
                    Отправить
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" className="mb-3">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </Alert>
      )}

      {/* Фильтры и управление */}
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-3">
            <h5 className="mb-0">
              <i className="bi bi-bell me-2"></i>
              Уведомления
            </h5>
            <Form.Select 
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              style={{ width: 'auto' }}
            >
              <option value="all">Все уведомления</option>
              <option value="unread">Только непрочитанные</option>
              <option value="read">Только прочитанные</option>
            </Form.Select>
          </div>

          {selectedNotifications.size > 0 && (
            <Button
              variant="primary"
              size="sm"
              onClick={handleMarkSelectedAsRead}
            >
              <i className="bi bi-check-lg me-1"></i>
              Отметить выбранные как прочитанные ({selectedNotifications.size})
            </Button>
          )}
        </Card.Header>

        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th width="50">
                    <Form.Check
                      type="checkbox"
                      checked={selectedNotifications.size === filteredNotifications.length && filteredNotifications.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th width="60">Тип</th>
                  <th>Уведомление</th>
                  <th width="150">Статус</th>
                  <th width="150">Дата</th>
                  <th width="100">Действия</th>
                </tr>
              </thead>
              <tbody>
                {filteredNotifications.map(notification => (
                  <tr 
                    key={notification.id}
                    className={notification.status === 'unread' ? 'table-active' : ''}
                  >
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={selectedNotifications.has(notification.id)}
                        onChange={(e) => handleSelectNotification(notification.id, e.target.checked)}
                      />
                    </td>
                    <td>
                      <Badge bg={getNotificationTypeVariant(notification.notification_type)}>
                        <i className={`bi ${getNotificationIcon(notification.notification_type)} me-1`}></i>
                        {notification.notification_type}
                      </Badge>
                    </td>
                    <td>
                      <div>
                        <h6 className="mb-1 fw-semibold">{notification.title}</h6>
                        <p className="mb-1 text-muted" style={{ fontSize: '0.9rem' }}>
                          {notification.message}
                        </p>
                        {notification.data && Object.keys(notification.data).length > 0 && (
                          <small className="text-muted">
                            {/* Данные: {JSON.stringify(notification.data)} */}
                          </small>
                        )}
                      </div>
                    </td>
                    <td>
                      <Badge bg={getStatusVariant(notification.status)}>
                        {notification.status === 'unread' ? 'Новое' : 'Прочитано'}
                      </Badge>
                      {notification.read_at && (
                        <div className="text-muted small mt-1">
                          {formatTimeAgo(notification.read_at)}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="small">
                        <div>{formatDateTime(notification.created_at)}</div>
                        <div className="text-muted">{formatTimeAgo(notification.created_at)}</div>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        {notification.status === 'unread' && (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => markAsRead(notification.id)}
                            title="Отметить как прочитанное"
                          >
                            <i className="bi bi-check-lg"></i>
                          </Button>
                        )}
                        <Button
                          variant="outline-info"
                          size="sm"
                          onClick={() => {
                            if (notification.data?.link) {
                              window.open(notification.data.link, '_blank');
                            }
                          }}
                          title="Просмотреть"
                          disabled={!notification.data?.link}
                        >
                          <i className="bi bi-eye"></i>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {filteredNotifications.length === 0 && (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-bell-slash display-1 mb-3 opacity-50"></i>
              <h5>Уведомлений не найдено</h5>
              <p>Попробуйте изменить фильтры или загрузить уведомления снова</p>
              <Button
                variant="outline-primary"
                onClick={() => loadNotifications()}
              >
                <i className="bi bi-arrow-clockwise me-1"></i>
                Обновить
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Модальное окно отправки уведомления */}
      <Modal show={showSendModal} onHide={() => setShowSendModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-send me-2"></i>
            Отправить уведомление
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {sendResult && (
            <Alert variant={sendResult.success ? 'success' : 'danger'}>
              <i className={`bi ${sendResult.success ? 'bi-check-circle' : 'bi-exclamation-triangle'} me-2`}></i>
              {sendResult.message}
              {sendResult.details && (
                <div className="mt-2 small">
                  <strong>Детали:</strong>
                  <pre className="mt-1 mb-0" style={{ fontSize: '0.8rem' }}>
                    {JSON.stringify(sendResult.details, null, 2)}
                  </pre>
                </div>
              )}
            </Alert>
          )}

          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Пользователь *</Form.Label>
                  {loadingUsers ? (
                    <div className="text-center py-2">
                      <Spinner animation="border" size="sm" />
                      <span className="ms-2">Загрузка пользователей...</span>
                    </div>
                  ) : (
                    <Form.Select
                      value={notificationData.user_id}
                      onChange={(e) => handleInputChange('user_id', e.target.value)}
                      required
                    >
                      <option value="">Выберите пользователя</option>
                      {users.map(user => (
                        <option key={user.id} value={user.id}>
                          {getFullName(user)} (ID: {user.id})
                          {user.telegram_username && ` - @${user.telegram_username}`}
                        </option>
                      ))}
                    </Form.Select>
                  )}
                  <Form.Text className="text-muted">
                    Пользователь, которому отправляется уведомление
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Тип уведомления</Form.Label>
                  <Form.Select
                    value={notificationData.notification_type}
                    onChange={(e) => handleInputChange('notification_type', e.target.value)}
                  >
                    <option value="info">Информация</option>
                    <option value="success">Успех</option>
                    <option value="warning">Предупреждение</option>
                    <option value="error">Ошибка</option>
                    <option value="system">Системное</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Заголовок *</Form.Label>
              <Form.Control
                type="text"
                value={notificationData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="Введите заголовок уведомления"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Сообщение *</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={notificationData.message}
                onChange={(e) => handleInputChange('message', e.target.value)}
                placeholder="Введите текст уведомления"
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Каналы отправки</Form.Label>
                  <div>
                    {['websocket', 'telegram'].map(channel => (
                      <Form.Check
                        key={channel}
                        type="checkbox"
                        id={`channel-${channel}`}
                        label={
                          <span className="text-capitalize">
                            <i className={`bi ${channel === 'websocket' ? 'bi-globe' : 'bi-telegram'} me-1`}></i>
                            {channel}
                          </span>
                        }
                        checked={notificationData.channels.includes(channel)}
                        onChange={() => handleChannelToggle(channel)}
                        className="mb-2"
                      />
                    ))}
                  </div>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Приоритет</Form.Label>
                  <Form.Select
                    value={notificationData.priority}
                    onChange={(e) => handleInputChange('priority', e.target.value)}
                  >
                    <option value="low">Низкий</option>
                    <option value="normal">Обычный</option>
                    <option value="high">Высокий</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Дополнительные данные (JSON)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={JSON.stringify(notificationData.data, null, 2)}
                onChange={(e) => {
                  try {
                    const data = e.target.value ? JSON.parse(e.target.value) : {};
                    handleInputChange('data', data);
                  } catch (err) {
                    // Игнорируем ошибки парсинга
                  }
                }}
                placeholder='{"key": "value"}'
              />
              <Form.Text className="text-muted">
                Дополнительные данные в формате JSON (опционально)
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => {
              setShowSendModal(false);
              setSendResult(null);
            }}
          >
            Отмена
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSendNotification}
            disabled={sending || !notificationData.title || !notificationData.message || !notificationData.user_id}
          >
            {sending ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Отправка...
              </>
            ) : (
              <>
                <i className="bi bi-send me-2"></i>
                Отправить уведомление
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default NotificationsTab;
